import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Loader2, Tags, Truck, Gift, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Tarifs() {
  const [services, setServices] = useState([]);
  const [zones, setZones] = useState([]);
  const [settings, setSettings] = useState({ free_delivery_threshold: 50, company_name: "", company_phone: "", company_address: "" });
  const [loading, setLoading] = useState(true);
  const [svcForm, setSvcForm] = useState({ open: false, edit: null, name: "", description: "", billing_mode: "piece", unit_price: 0, active: true });
  const [zoneFees, setZoneFees] = useState({});
  const [threshold, setThreshold] = useState(50);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/services"), api.get("/zones"), api.get("/settings")]).then(([s, z, st]) => {
      setServices(s.data); setZones(z.data); setSettings(st.data); setThreshold(st.data.free_delivery_threshold || 0);
      const fees = {}; z.data.forEach((zz) => (fees[zz.id] = zz.delivery_fee)); setZoneFees(fees);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const saveService = async () => {
    try {
      const body = { name: svcForm.name, description: svcForm.description, billing_mode: svcForm.billing_mode, unit_price: Number(svcForm.unit_price) || 0, active: svcForm.active };
      if (svcForm.edit) await api.put(`/services/${svcForm.edit}`, body);
      else await api.post("/services", body);
      toast.success("Service enregistré"); setSvcForm({ open: false, edit: null, name: "", description: "", billing_mode: "piece", unit_price: 0, active: true }); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const delService = async (id) => { await api.delete(`/services/${id}`); toast.success("Supprimé"); load(); };

  const saveZoneFee = async (z) => {
    try { await api.put(`/zones/${z.id}`, { name: z.name, commune_ids: z.commune_ids, delivery_fee: Number(zoneFees[z.id]) || 0, active: z.active }); toast.success(`Frais ${z.name} mis à jour`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const saveThreshold = async () => {
    try { await api.put("/settings", { free_delivery_threshold: Number(threshold) || 0 }); toast.success("Seuil enregistré"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div>;

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl sm:text-3xl font-bold font-heading">Tarifs & Services</h1><p className="text-slate-500 text-sm mt-1">Prestations, frais de livraison et gratuité</p></div>

      {/* Services */}
      <div className="bg-white border rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2"><Tags className="w-5 h-5 text-brand-600" /> Services</h3>
          <Button data-testid="service-new-btn" size="sm" onClick={() => setSvcForm({ open: true, edit: null, name: "", description: "", billing_mode: "piece", unit_price: 0, active: true })} className="bg-brand-600 hover:bg-brand-700"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {services.map((s) => (
            <div key={s.id} data-testid={`service-card-${s.id}`} className="border rounded-xl p-3">
              <div className="flex justify-between items-start">
                <div className="font-medium">{s.name}</div>
                <div className="flex gap-1">
                  <button onClick={() => setSvcForm({ open: true, edit: s.id, name: s.name, description: s.description || "", billing_mode: s.billing_mode, unit_price: s.unit_price, active: s.active })} className="p-1 text-slate-400 hover:text-brand-600"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`service-del-${s.id}`} onClick={() => delService(s.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="text-xs text-slate-400">{s.description}</div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-brand-700">{money(s.unit_price)} <span className="text-xs text-slate-400 font-normal">/ {s.billing_mode === "kilo" ? "kg" : "pièce"}</span></span>
                <span className={`text-[11px] rounded-full px-2 py-0.5 ${s.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{s.active ? "Actif" : "Inactif"}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Delivery fees */}
        <div className="bg-white border rounded-2xl p-5">
          <h3 className="font-semibold flex items-center gap-2 mb-4"><Truck className="w-5 h-5 text-brand-600" /> Frais de livraison par zone</h3>
          <div className="space-y-2">
            {zones.map((z) => (
              <div key={z.id} className="flex items-center gap-2">
                <span className="flex-1 font-medium">{z.name}</span>
                <div className="relative w-28"><span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span><Input data-testid={`zonefee-${z.id}`} type="number" value={zoneFees[z.id] ?? ""} onChange={(e) => setZoneFees({ ...zoneFees, [z.id]: e.target.value })} className="pl-5 h-9" /></div>
                <Button data-testid={`zonefee-save-${z.id}`} size="sm" variant="outline" onClick={() => saveZoneFee(z)}>OK</Button>
              </div>
            ))}
            {zones.length === 0 && <p className="text-sm text-slate-400">Créez des zones dans Géographie.</p>}
          </div>
        </div>

        {/* Free delivery */}
        <div className="bg-white border rounded-2xl p-5">
          <h3 className="font-semibold flex items-center gap-2 mb-4"><Gift className="w-5 h-5 text-emerald-600" /> Livraison gratuite</h3>
          <Label>Montant minimum pour la gratuité ($)</Label>
          <div className="flex gap-2 mt-1">
            <Input data-testid="threshold-input" type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
            <Button data-testid="threshold-save" onClick={saveThreshold} className="bg-emerald-600 hover:bg-emerald-700">Enregistrer</Button>
          </div>
          <p className="text-xs text-slate-400 mt-2">Livraison gratuite à partir de {money(threshold)}.</p>
        </div>
      </div>

      <Dialog open={svcForm.open} onOpenChange={(v) => setSvcForm((f) => ({ ...f, open: v }))}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{svcForm.edit ? "Modifier" : "Nouveau"} service</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom *</Label><Input data-testid="svc-name" value={svcForm.name} onChange={(e) => setSvcForm({ ...svcForm, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Description</Label><Textarea value={svcForm.description} onChange={(e) => setSvcForm({ ...svcForm, description: e.target.value })} className="mt-1" rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Mode de facturation</Label>
                <Select value={svcForm.billing_mode} onValueChange={(v) => setSvcForm({ ...svcForm, billing_mode: v })}>
                  <SelectTrigger data-testid="svc-mode" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="piece">Par pièce</SelectItem><SelectItem value="kilo">Par kilogramme</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Prix unitaire ($)</Label><Input data-testid="svc-price" type="number" value={svcForm.unit_price} onChange={(e) => setSvcForm({ ...svcForm, unit_price: e.target.value })} className="mt-1" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={svcForm.active} onCheckedChange={(v) => setSvcForm({ ...svcForm, active: v })} /> Actif</label>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setSvcForm((f) => ({ ...f, open: false }))}>Annuler</Button><Button data-testid="svc-save" onClick={saveService} className="bg-brand-600 hover:bg-brand-700">Enregistrer</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
